import { formatDate } from '@frontend/mwm-ui'
import { useEffect, useReducer, useState } from 'react'
import { MdOutlineFlag } from 'react-icons/md'
import { useInView } from 'react-intersection-observer'

import type { ChangeEvent, ReactElement } from 'react'
import type { TStretchedDeviceType } from '~/types/device-type'
import type { IUaCampaign } from '~/types/ua-campaign'

import { networkOptions } from './ua-campaign'

import CollapseButton from '~/components/Buttons/CollapseButton'
import Filters, { FiltersContent } from '~/components/Filters'
import CheckboxInput from '~/components/Inputs/Basic/CheckboxInput'
import DateInput from '~/components/Inputs/Basic/DateInput'
import MultiSelectInput from '~/components/Inputs/Basic/MultiSelectInput'
import SearchInput from '~/components/Inputs/Basic/SearchInput'
import SelectInput from '~/components/Inputs/Basic/SelectInput'
import Layout from '~/components/Layouts/Layout'
import MwMLoader from '~/components/MwMLoader'
import CustomAppOption from '~/components/SelectOptions/CustomAppOption'
import UaManagementCard from '~/components/UaManagementCard'
import ViewTitle from '~/components/ViewTitle'
import { MAX_DAYS_RANGE, TODAY } from '~/const'
import { countryWithFlagOptions, getDeviceTypeOptions } from '~/const/options'
import useApps from '~/hooks/useApps'
import { useSearchParamsContext } from '~/providers/SearchParams'
import {
  useListAdSetNamesQuery,
  useListCampaignNamesQuery,
  useListCampaignRecommendationsQuery,
} from '~/queries/ua-campaign'
import { debounce } from '~/utils/function'

import './styles.scss'

export interface IFilters {
  adSetNames: string[]
  appKeys: string[]
  campaignNames: string[]
  countries: string[]
  deviceType: TStretchedDeviceType
  endDate: string
  hideTreatedReco: boolean
  networks: string[]
  startDate: string
}

const defaultState: IFilters = {
  adSetNames: [],
  appKeys: [],
  campaignNames: [],
  countries: [],
  deviceType: 'all',
  endDate: formatDate(TODAY, 'YYYY-MM-DD'),
  hideTreatedReco: false,
  networks: [],
  startDate: formatDate(TODAY, 'YYYY-MM-DD'),
}

const reducer = (
  state: IFilters,
  action: { filter: keyof IFilters; type: 'reset' | 'update'; value: unknown }
): IFilters => {
  switch (action.type) {
    case 'reset':
      return defaultState
    case 'update':
      return { ...state, [action.filter]: action.value }
    default:
      return state
  }
}

const UAManagement = (): ReactElement => {
  /** Local state */

  const { appKeys, isAppsLoading } = useApps()

  const { inView, ref } = useInView()

  const appKeyOptions = appKeys.map(({ appKey, url }) => ({ label: appKey, url, value: appKey }))

  const deviceTypeOptions = getDeviceTypeOptions(['all', 'android', 'ios', 'web'])

  const { dispatchToSearchParams, searchParamsObject } = useSearchParamsContext()

  const initialState: IFilters = {
    adSetNames: searchParamsObject['adSetNames[]'] || [],
    appKeys: searchParamsObject['appKeys[]'] || [],
    campaignNames: searchParamsObject['campaignNames[]'] || [],
    countries: searchParamsObject['countries[]'] || [],
    deviceType: (searchParamsObject['deviceType'] as TStretchedDeviceType) || 'all',
    endDate: searchParamsObject['end'] || defaultState.endDate,
    hideTreatedReco: searchParamsObject['hideTreatedReco'] === 'false',
    networks: searchParamsObject['networks[]'] || [],
    startDate: searchParamsObject['start'] || defaultState.startDate,
  }

  const [campaignRecommendations, setCampaignRecommendations] = useState<IUaCampaign[] | undefined>()

  const [state, dispatch] = useReducer(reducer, initialState)

  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(false)

  const debouncedSearch = debounce(
    (search) => dispatch({ filter: 'campaignNames', type: 'update', value: [search] }),
    500
  )

  /** Queries & Mutations */

  const {
    data: campaignRecommendationsData,
    fetchNextPage: fetchCampaignRecommendationsNextPage,
    hasNextPage: hasCampaignRecommendationsNextPage,
    isError: isCampaignRecommendationsError,
    isFetchingNextPage: isCampaignRecommendationsFetchingNextPage,
    isLoading: isCampaignRecommendationsLoading,
  } = useListCampaignRecommendationsQuery(state)

  const { data: adSetNamesData, isLoading: isAdSetNamesLoading } = useListAdSetNamesQuery(state)

  const adSetNamesOptions: IOption[] = adSetNamesData?.map((el) => ({ label: el, value: el })) || []

  const { data: campaignNamesData, isLoading: isCampaignNamesLoading } = useListCampaignNamesQuery(state)

  const campaignNamesOptions: IOption[] = campaignNamesData?.map((el) => ({ label: el, value: el })) || []

  /** Handlers */

  const handleDateRangeChange = (dateRange: string[]) => {
    const [startDate, endDate] = dateRange
    dispatch({ filter: 'startDate', type: 'update', value: [formatDate(startDate, 'YYYY-MM-DD')] })
    dispatch({ filter: 'endDate', type: 'update', value: [formatDate(endDate, 'YYYY-MM-DD')] })
  }

  const handleFilterChange = (name: keyof IFilters, value: IOption[] | (IOption | null) | boolean) => {
    const formattedValue = Array.isArray(value)
      ? value.map(({ value }) => value)
      : value
        ? typeof value === 'boolean'
          ? value
          : value.value
        : value
    dispatch({ filter: name, type: 'update', value: formattedValue })
  }

  const handleSearchChange = (evt: ChangeEvent<HTMLInputElement>) => debouncedSearch(evt.target.value)

  const toggleCollapseFilters = () => setIsFiltersCollapsed(!isFiltersCollapsed)

  /** Effects */

  useEffect(() => {
    dispatchToSearchParams({
      type: 'update',
      value: {
        'adSetNames[]': state.adSetNames,
        'appKeys[]': state.appKeys,
        'campaignNames[]': state.campaignNames,
        'countries[]': state.countries,
        deviceType: state.deviceType,
        end: state.endDate,
        hideTreatedReco: state.hideTreatedReco ? 'false' : '',
        'networks[]': state.networks,
        start: state.startDate,
      },
    })
  }, [dispatchToSearchParams, state])

  useEffect(() => {
    if (inView && !isCampaignRecommendationsLoading && !isCampaignRecommendationsFetchingNextPage) {
      fetchCampaignRecommendationsNextPage()
    }
  }, [
    inView,
    isCampaignRecommendationsLoading,
    isCampaignRecommendationsFetchingNextPage,
    fetchCampaignRecommendationsNextPage,
  ])

  useEffect(() => {
    setCampaignRecommendations(campaignRecommendationsData?.pages.flatMap((page) => page.data || []))
  }, [campaignRecommendationsData])

  /** Render */

  return (
    <div className="ua-management-view">
      <ViewTitle className="layout-view-title top-zero ua-management-view-title">
        <>UA Campaign Management</>
        {campaignRecommendations?.length ? (
          <div className="recommendations-count">{campaignRecommendations.length} recommendations visible</div>
        ) : null}
      </ViewTitle>
      <Filters className="ua-management-view__filters">
        {!isFiltersCollapsed ? (
          <FiltersContent>
            <SearchInput className="search-input" onChange={handleSearchChange} />
            <MultiSelectInput
              label="Network"
              onChange={(option) => handleFilterChange('networks', option as IOption[])}
              options={networkOptions}
              value={networkOptions.filter(({ value }) => state.networks.find((el) => value === el))}
              withOnly
            />
            <MultiSelectInput
              components={{ Option: CustomAppOption }}
              isLoading={isAppsLoading}
              label="App key"
              onChange={(option) => handleFilterChange('appKeys', option as IOption[])}
              options={appKeyOptions}
              value={appKeyOptions.filter(({ value }) => state.appKeys.find((el) => value === el))}
              withOnly
            />
            <SelectInput
              label="Device type"
              onChange={(option) => handleFilterChange('deviceType', option as IOption | null)}
              options={deviceTypeOptions}
              value={deviceTypeOptions.find(({ value }) => value === state.deviceType)}
            />
            <MultiSelectInput
              defaultIcon={<MdOutlineFlag size={16} />}
              label="Countries"
              onChange={(option) => handleFilterChange('countries', option as IOption[])}
              options={countryWithFlagOptions}
              value={countryWithFlagOptions.filter(({ value }) => state.countries.find((el) => value === el))}
              withOnly
            />
            <DateInput
              endDate={state.endDate}
              label="Date"
              maxDate={TODAY}
              maxRangeDays={MAX_DAYS_RANGE}
              onDateRangeChange={handleDateRangeChange}
              startDate={state.startDate}
              withFilters
              withRange
              withTime={false}
            />
            <div className="long-input">
              <MultiSelectInput
                isLoading={isCampaignNamesLoading}
                label="Campaign names"
                onChange={(option) => handleFilterChange('campaignNames', option as IOption[])}
                options={campaignNamesOptions}
                value={campaignNamesOptions.filter(({ value }) => state.campaignNames.find((el) => value === el))}
                withOnly
              />
            </div>
            <div className="long-input">
              <MultiSelectInput
                isLoading={isAdSetNamesLoading}
                label="AdSet names"
                onChange={(option) => handleFilterChange('adSetNames', option as IOption[])}
                options={adSetNamesOptions}
                value={adSetNamesOptions.filter(({ value }) => state.adSetNames.find((el) => value === el))}
                withOnly
              />
            </div>
            <CheckboxInput
              checked={state.hideTreatedReco}
              label="Hide Treated Reco"
              onChange={(e) => handleFilterChange('hideTreatedReco', e.target.checked)}
            />
          </FiltersContent>
        ) : null}
        <CollapseButton isCollapsed={isFiltersCollapsed} onClick={toggleCollapseFilters} />
      </Filters>
      {isCampaignRecommendationsLoading ? (
        <div className="loader-container">
          <MwMLoader category="item" />
        </div>
      ) : !campaignRecommendations?.length ? (
        <Layout className="ua-management-view__empty-content" withMargin>
          No results found
        </Layout>
      ) : (
        <>
          <Layout className="ua-management-view__content" withMargin>
            {campaignRecommendations?.map((infos, idx) => <UaManagementCard infos={infos} key={idx} />)}
          </Layout>
          {hasCampaignRecommendationsNextPage && !isCampaignRecommendationsError ? (
            <div className="next-page-loader" ref={ref}>
              {isCampaignRecommendationsFetchingNextPage ? <MwMLoader category="item" /> : null}
            </div>
          ) : null}
        </>
      )}
    </div>
  )
}

export default UAManagement
